import React, { useState, createContext, useContext, useCallback, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { Toaster } from 'sonner';
import OfflineBanner from '../components/OfflineBanner';
import { getTodayCashRegister } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Home,
  Menu, X,
  Power,
  ShoppingBag, Inbox, UtensilsCrossed, Download,
  FileText, Users, BarChart3, User, Settings,
  Wifi, WifiOff, RefreshCw, AlertCircle, ChevronDown, Loader2,
  Sun, Moon
} from 'lucide-react';
import { toast } from 'sonner';
import { readPosUiPreferences, writePosUiPreferences, applyPosUiPreferences } from './settingsRuntime';

/* ─── Drawer context (lets child pages open the nav drawer) ─────────────── */
export const DrawerContext = createContext({ open: () => {}, close: () => {} });
export const useDrawer = () => useContext(DrawerContext);

/* ─── Subtitle context (lets child pages set the centered header title) ─── */
export const SubtitleContext = createContext({ subtitle: '', setSubtitle: () => {} });
export const usePosSubtitle = () => useContext(SubtitleContext);

/* ─── Navigation definition ─────────────────────────────────────────────── */
const NAV_ITEMS = [
  { path: '/pos/home',          label: 'Start',                 icon: Home            },
  { path: '/pos/terminal',      label: 'Bonieren',              icon: ShoppingBag     },
  { path: '/pos/orders',        label: 'Offene Bestellungen',   icon: Inbox           },
  { path: '/pos/tables',        label: 'Tische',                icon: UtensilsCrossed },
  { path: '/pos/online-orders', label: 'Online Bestellungen',   icon: Download        },
  { path: '/pos/invoices',      label: 'Rechnungen',            icon: FileText        },
  { path: '/pos/berichtswesen', label: 'Berichtswesen',         icon: BarChart3       },
];

const STUB_ITEMS = [
  { label: 'Kunden', icon: Users },
];

/* ─── Route → page title ────────────────────────────────────────────────── */
const ROUTE_TITLE = {
  '/pos/home':          'Start',
  '/pos/terminal':      'Bonieren',
  '/pos/tables':        'Tische',
  '/pos/orders':        'Offene Bestellungen',
  '/pos/online-orders': 'Online Bestellungen',
  '/pos/invoices':      'Rechnungen',
  '/pos/berichtswesen': 'Berichtswesen',
  '/pos/settings':      'Einstellungen',
};

const MOBILE_DOCK_ITEMS = [
  { path: '/pos/home', label: 'Home', icon: Home },
  { path: '/pos/tables', label: 'Tische', icon: UtensilsCrossed },
  { path: '/pos/terminal', label: 'Bon', icon: ShoppingBag },
  { path: '/pos/orders', label: 'Offen', icon: Inbox },
];

const SHIFT_REQUIRED_ROUTES = new Set([
  '/pos/terminal',
  '/pos/tables',
  '/pos/orders',
  '/pos/online-orders',
  '/pos/invoices',
]);

export default function PosLayout() {
  const { user, logout } = useAuth();
  const { isOnline, isSyncing, pendingOrdersCount, syncData } = useOffline();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [subtitle, setSubtitleState] = useState('');
  const [shiftGate, setShiftGate] = useState({ loading: false, hasActiveShift: true, requiresClosingPreviousShift: false });
  const [theme, setTheme] = useState(() => readPosUiPreferences().theme);
  const setSubtitle = useCallback((s) => setSubtitleState(s), []);

  const handleToggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    const nextPreferences = writePosUiPreferences({ ...readPosUiPreferences(), theme: nextTheme });
    applyPosUiPreferences(nextPreferences);
    setTheme(nextTheme);
  };

  const pageTitle = ROUTE_TITLE[location.pathname] ?? 'Kasse';
  const requiresActiveShift = SHIFT_REQUIRED_ROUTES.has(location.pathname);

  useEffect(() => {
    let cancelled = false;

    if (!requiresActiveShift) {
      setShiftGate({ loading: false, hasActiveShift: true, requiresClosingPreviousShift: false });
      return () => {
        cancelled = true;
      };
    }

    setShiftGate((prev) => ({ ...prev, loading: true }));
    getTodayCashRegister()
      .then((register) => {
        if (cancelled) return;
        setShiftGate({
          loading: false,
          hasActiveShift: Boolean(register?.has_active_shift),
          requiresClosingPreviousShift: Boolean(register?.requires_closing_previous_shift),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setShiftGate({ loading: false, hasActiveShift: false, requiresClosingPreviousShift: false });
      });

    return () => {
      cancelled = true;
    };
  }, [requiresActiveShift, location.pathname]);

  const handleSync = async () => {
    try { await syncData(); toast.success('Synchronisiert'); }
    catch { toast.error('Synchronisation fehlgeschlagen'); }
  };

  const handleSwitchUser = () => {
    setDrawerOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/pos/login');
  };

  return (
    <DrawerContext.Provider value={{ open: () => setDrawerOpen(true), close: () => setDrawerOpen(false) }}>
    <SubtitleContext.Provider value={{ subtitle, setSubtitle }}>
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        {/* ── Global slim header ── */}
        <header className="relative z-30 flex flex-shrink-0 items-center gap-1.5 border-b border-[#0b2627] bg-[#004646] px-1.5 py-1 text-white sm:gap-3 sm:px-3 sm:py-2">
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex-shrink-0 p-1 transition-colors hover:bg-white/10 sm:p-2"
            data-testid="pos-menu-btn"
          >
            <Menu className="h-4 w-4 text-white sm:h-5 sm:w-5" />
          </button>

          {/* Section title — left */}
          <h1 className="whitespace-nowrap text-[11px] font-semibold text-white sm:text-sm">{pageTitle}</h1>

          {/* Subsection title — centered absolutely across full header */}
          {subtitle && (
            <span className="pointer-events-none absolute inset-x-0 px-16 text-center text-[11px] font-bold text-white sm:px-28 sm:text-sm">
              {subtitle}
            </span>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right status */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            {isOnline
              ? <Wifi className="h-[15px] w-[15px] text-[#89c33d] sm:h-4 sm:w-4" />
              : <WifiOff className="h-[15px] w-[15px] text-orange-300 sm:h-4 sm:w-4" />
            }
            {pendingOrdersCount > 0 && (
              <span className="bg-orange-500 px-1 py-0.5 text-[10px] font-bold text-white sm:px-1.5 sm:text-xs">
                {pendingOrdersCount}
              </span>
            )}
            <button
              onClick={handleSync}
              disabled={isSyncing || !isOnline}
              className="p-1 transition-colors hover:bg-white/10 disabled:opacity-40 sm:p-1.5"
            >
              <RefreshCw className={`h-[15px] w-[15px] text-white/70 sm:h-4 sm:w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={handleToggleTheme}
              className="p-1 transition-colors hover:bg-white/10 sm:p-1.5"
              title={theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
              data-testid="pos-theme-toggle"
            >
              {theme === 'dark'
                ? <Sun className="h-[15px] w-[15px] text-white/70 sm:h-4 sm:w-4" />
                : <Moon className="h-[15px] w-[15px] text-white/70 sm:h-4 sm:w-4" />
              }
            </button>
            <button
              type="button"
              onClick={() => navigate('/pos/settings')}
              className="p-1 transition-colors hover:bg-white/10 sm:p-1.5"
              title="Einstellungen"
            >
              <Settings className="h-[15px] w-[15px] text-white/70 sm:h-4 sm:w-4" />
            </button>
            <button
              type="button"
              onClick={handleSwitchUser}
              className="p-1 transition-colors hover:bg-white/10 sm:p-1.5"
              title="Abmelden"
            >
              <Power className="h-[15px] w-[15px] text-white/70 sm:h-4 sm:w-4" />
            </button>
          </div>
        </header>

        {/* ── Offline banner ── */}
        <OfflineBanner />

        {/* ── Main content ── */}
        <main className="flex-1 overflow-hidden">
          {requiresActiveShift && shiftGate.loading ? (
            <div className="flex h-full items-center justify-center bg-card">
              <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
            </div>
          ) : requiresActiveShift && !shiftGate.hasActiveShift ? (
            <div className="flex h-full items-center justify-center bg-card p-6">
              <div className="w-full max-w-xl border border-border bg-card p-8 text-center">
                <p className="text-3xl font-black tracking-tight text-foreground">
                  {shiftGate.requiresClosingPreviousShift ? 'Vorherige Schicht abschließen' : 'Kein aktiver Kassentag'}
                </p>
                <p className="mt-3 text-base text-muted-foreground">
                  {shiftGate.requiresClosingPreviousShift
                    ? 'Bitte im Berichtswesen zuerst die vorherige Schicht beenden. Offene Bons werden dabei als Barzahlung abgeschlossen; danach kann ein neuer Tag begonnen werden.'
                    : 'Bitte zuerst im Berichtswesen einen neuen Tag beginnen. Ohne offene Schicht bleiben Bonieren, Tische, offene Bestellungen, Online-Bestellungen und Rechnungen gesperrt.'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/pos/berichtswesen')}
                  className="mt-6 inline-flex items-center justify-center bg-lime-500 px-5 py-3 text-sm font-bold uppercase tracking-[0.08em] text-foreground transition-colors hover:bg-lime-400"
                >
                  Zu Berichtswesen
                </button>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>

        <nav className="safe-area-bottom flex-shrink-0 border-t border-[#0b2627] bg-[#004646] text-white lg:hidden">
          <div className="grid grid-cols-4">
            {MOBILE_DOCK_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`flex min-h-[60px] flex-col items-center justify-center gap-1 px-1 py-1.5 transition-colors sm:min-h-[68px] sm:gap-1.5 sm:py-2.5 ${
                    isActive ? 'bg-[#1d1d1f] text-white' : 'text-white/70 hover:bg-black/20'
                  }`}
                >
                  <Icon className={`h-[18px] w-[18px] sm:h-5 sm:w-5 ${isActive ? 'text-[#7cf000]' : 'text-white/70'}`} />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] sm:text-[12px]">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* ── Left nav drawer (overlay) ── */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setDrawerOpen(false)}
            />

            {/* Drawer panel */}
            <aside className="relative flex w-72 flex-col overflow-hidden border-r border-border bg-muted">

              {/* ── Company / register / user header ── */}
              <div className="border-b border-border bg-[#004646] px-5 pb-4 pt-5 text-white">
                {/* Logo placeholder */}
                <div className="mb-3 flex h-10 w-10 items-center justify-center bg-[#7cf000]">
                  <ShoppingBag className="h-5 w-5 text-[#28333a]" />
                </div>
                <p className="text-lg font-bold leading-tight text-white">FiscalPOS</p>
                <p className="text-sm text-white/70">KASSA 01</p>
                <p className="text-sm text-white/70">{user?.name || 'Kellner'}</p>
              </div>

              {/* ── Status row ── */}
              <div className="border-b border-border px-5 py-2.5">
                <button className="flex items-center gap-2 text-sm w-full">
                  {isOnline ? (
                    <>
                      <Wifi className="h-4 w-4 flex-shrink-0 text-[#6ea537]" />
                      <span className="font-medium text-[#314047]">Online</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 flex-shrink-0 text-orange-500" />
                      <span className="font-medium text-orange-700">Bitte Status prüfen</span>
                      <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
                    </>
                  )}
                </button>
              </div>

              {/* ── Navigation ── */}
              <nav className="flex-1 py-1 overflow-y-auto">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setDrawerOpen(false)}
                      data-testid={`pos-nav-${item.path.split('/').pop()}`}
                      className={`flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
                        isActive
                          ? 'bg-card font-semibold text-[#24323b]'
                          : 'text-muted-foreground hover:bg-border'
                      }`}
                    >
                      <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-[#7bae3a]' : 'text-muted-foreground'}`} />
                      {item.label}
                    </NavLink>
                  );
                })}

                {/* Stub items (greyed out, not functional yet) */}
                {STUB_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 px-5 py-3 text-sm text-muted-foreground cursor-not-allowed"
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {item.label}
                    </div>
                  );
                })}
              </nav>

              {/* ── Konto section ── */}
              <div className="border-t border-border pb-safe">
                <p className="px-5 pt-3 pb-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Konto
                </p>
                <button
                  onClick={handleSwitchUser}
                  className="flex w-full items-center gap-3 px-5 py-3 text-sm text-muted-foreground transition-colors hover:bg-border"
                  data-testid="pos-logout-btn"
                >
                  <User className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                  Benutzer wechseln
                </button>
                <NavLink
                  to="/pos/settings"
                  onClick={() => setDrawerOpen(false)}
                  className={`flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
                    location.pathname === '/pos/settings'
                      ? 'bg-card font-semibold text-[#24323b]'
                      : 'text-muted-foreground hover:bg-border'
                  }`}
                >
                  <Settings className={`h-5 w-5 flex-shrink-0 ${location.pathname === '/pos/settings' ? 'text-[#7bae3a]' : 'text-muted-foreground'}`} />
                  Einstellungen
                </NavLink>
              </div>
            </aside>
          </div>
        )}

        <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <DialogContent className="max-w-sm rounded-none border border-border">
            <DialogHeader>
              <DialogTitle>Wirklich austreten?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Du wirst von der Kasse abgemeldet und musst dich danach wieder mit PIN anmelden.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex h-11 flex-1 items-center justify-center border border-border text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleConfirmLogout}
                  className="flex h-11 flex-1 items-center justify-center gap-2 bg-red-600 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  <Power className="h-4 w-4" />
                  Austreten
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Toaster position="top-center" richColors />
      </div>
    </SubtitleContext.Provider>
    </DrawerContext.Provider>
  );
}
